import { useState } from 'react';
import {
  List,
  ListContextProvider,
  useListController,
  useNotify,
  useRefresh,
  useTranslate,
} from 'react-admin';

import { employeesDataProvider } from '@/dataProvider';
import { useGetUserRole, usePolling } from '@/hooks';
import { EmployeesCheckUploadXLSStatusEnum, RegistrationTypeEnum } from '@/types';
import { EmployeesEmpty } from '@/view/employees/EmployeesEmpty';
import {
  adminEmployeesFilters,
  ImportCSVModal,
  ListActions,
  superAdminEmployeesFilters,
  TabbedDatagrid,
  SuspendEmployees,
} from '@/view/employees/EmployeesList/components';
import {
  formatEmployeesErrors,
  formatEmployeesWarnings,
} from '@/view/employees/EmployeesList/utils';

function EmployeesList(): JSX.Element {
  const t = useTranslate();
  const listContext = useListController();
  const refresh = useRefresh();
  const notify = useNotify();
  const role = useGetUserRole();

  const [isImportCSVModalOpen, setIsImportCSVModalOpen] = useState<boolean>(false);
  const [isSuspendEmployeesModalOpen, setIsSuspendEmployeesModalOpen] = useState<boolean>(false);
  const [isPollingActive, setIsPollingActive] = useState<boolean>(false);
  const [sqsMessageId, setSqsMessageId] = useState<string>('');

  const handleOpenImportCSVModal = (): void => {
    setIsImportCSVModalOpen(true);
  };

  const handleCloseImportCSVModal = (): void => {
    setIsImportCSVModalOpen(false);
  };

  const handleOpenSuspendEmployeesModal = (): void => {
    setIsSuspendEmployeesModalOpen(true);
  };

  const handleCloseSuspendEmployeesModal = (): void => {
    setIsSuspendEmployeesModalOpen(false);
  };

  const handleSQSProgress = async (): Promise<void> => {
    const { status, errors, warnings } =
      await employeesDataProvider.checkUploadEmployees(sqsMessageId);

    if (status === EmployeesCheckUploadXLSStatusEnum.COMPLETED) {
      if (warnings?.length) {
        const warningMessages = formatEmployeesWarnings(warnings, t);

        notify(warningMessages, {
          type: 'warning',
          autoHideDuration: 30000,
        });
      } else {
        notify('resources.employees.import_csv_status.success', {
          type: 'success',
        });
      }
      setIsPollingActive(false);
      setSqsMessageId('');
      refresh();
    } else if (status === EmployeesCheckUploadXLSStatusEnum.FAILED) {
      if (errors.length) {
        const errorMessages = formatEmployeesErrors(errors, t);

        notify(errorMessages, {
          type: 'error',
          autoHideDuration: 30000,
        });
      } else {
        notify('resources.employees.import_csv_status.failure', {
          type: 'error',
          autoHideDuration: 10000,
        });
      }

      setIsPollingActive(false);
      setSqsMessageId('');
    }
  };

  usePolling(handleSQSProgress, 10000, isPollingActive);

  return (
    <ListContextProvider value={listContext}>
      <List
        sort={{ field: 'last_update', order: 'ASC' }}
        perPage={25}
        filters={
          role === RegistrationTypeEnum.SUPER_ADMIN
            ? superAdminEmployeesFilters
            : adminEmployeesFilters
        }
        actions={<ListActions handleOpenImportCSVModal={handleOpenImportCSVModal} />}
        empty={<EmployeesEmpty handleOpenImportCSVModal={handleOpenImportCSVModal} />}
      >
        <TabbedDatagrid handleOpenSuspendEmployeesModal={handleOpenSuspendEmployeesModal} />
      </List>
      <ImportCSVModal
        setSqsMessageId={setSqsMessageId}
        setIsPollingActive={setIsPollingActive}
        isOpen={isImportCSVModalOpen}
        handleClose={handleCloseImportCSVModal}
      />
      {isSuspendEmployeesModalOpen && (
        <SuspendEmployees
          isOpen={isSuspendEmployeesModalOpen}
          handleClose={handleCloseSuspendEmployeesModal}
          ids={listContext.selectedIds}
        />
      )}
    </ListContextProvider>
  );
}

export default EmployeesList;
